/**
 * Exit consolidation SIMULATION — the pure half (owner ruling 2026-10-14).
 *
 * The automated exit consolidation solves the pilot's treatment doors only. A planner may still
 * ask the platform for a consolidation over a chosen perimeter — `pilot` (the treatment arm) or
 * `all` (every door) — as a SIMULATION: the platform runs the position and decision jobs over the
 * live window into runs flagged as simulated, which the live run directory does not list and on
 * which no disposition can be recorded (`planning.exit.consolidation.simulation.not_executable`).
 *
 * Everything decided in the browser is decided here, on strings, and tested: the paths, the
 * request body, the state words, when polling stops, which refusal reads as which sentence, and
 * how a run's perimeter is labelled in the run picker.
 */

import { ApiProblemError } from '@/api';
import type { ConsolidationRunSummary, ExitSimulationRequest } from '@/api/generated';

// ── the paths ────────────────────────────────────────────────────────────────

/**
 * COMPOSE, with the market's simulations listed under the composer. A sibling PATH of
 * `consolidation`, not under it, because `consolidation/:storeKey` owns every second segment
 * there (`door-drill.ts`).
 */
export const CONSOLIDATION_SIMULATE_PATH = '/exit/consolidation-simulate';

/** One simulation's status page. */
export function exitSimulationPath(simulationId: string): string {
  return `${CONSOLIDATION_SIMULATE_PATH}/${encodeURIComponent(simulationId)}`;
}

/** The simulation a status page's path names, or `null` (the compose page, or a malformed id). */
export function readExitSimulationDrill(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const base = CONSOLIDATION_SIMULATE_PATH.split('/').filter(Boolean);
  if (!base.every((segment, index) => segments[index] === segment)) return null;
  if (segments.length !== base.length + 1) return null;
  try {
    const value = decodeURIComponent(segments[base.length] ?? '').trim();
    return value === '' ? null : value;
  } catch {
    return null;
  }
}

// ── the request ──────────────────────────────────────────────────────────────

export const PERIMETERS = ['pilot', 'all'] as const;
export type SimulationPerimeter = (typeof PERIMETERS)[number];

/** The radio's words, one per perimeter. */
export const PERIMETER_CHOICE_KEY: Readonly<Record<SimulationPerimeter, string>> = {
  pilot: 'exit.consolidationSimulate.perimeter.pilot',
  all: 'exit.consolidationSimulate.perimeter.all',
};

export function isPerimeter(value: string): value is SimulationPerimeter {
  return (PERIMETERS as readonly string[]).includes(value);
}

/**
 * The body of `POST /exit/consolidation/simulations`. The market is sent only when one is
 * stated: the platform scopes the request to the principal's market otherwise.
 */
export function simulationRequest(perimeter: SimulationPerimeter, countryKey: string | null = null): ExitSimulationRequest {
  return countryKey === null || countryKey.trim() === ''
    ? { perimeter }
    : { perimeter, country_key: countryKey.trim() };
}

// ── the state ────────────────────────────────────────────────────────────────

/** The simulation's own states (`ExitSimulationView.state`). */
export const EXIT_SIMULATION_STATE_KEY: Readonly<Record<string, string>> = {
  queued: 'exit.consolidationSimulate.state.queued',
  running: 'exit.consolidationSimulate.state.running',
  succeeded: 'exit.consolidationSimulate.state.succeeded',
  failed: 'exit.consolidationSimulate.state.failed',
  expired: 'exit.consolidationSimulate.state.expired',
};

export const UNRECOGNISED_EXIT_SIMULATION_STATE_KEY = 'exit.consolidationSimulate.state.unrecognised';

export function exitSimulationStateKey(state: string): string {
  return EXIT_SIMULATION_STATE_KEY[state] ?? UNRECOGNISED_EXIT_SIMULATION_STATE_KEY;
}

/** Only these two are still moving. Anything else — an unknown word included — stops the poll. */
export function isExitSimulationInFlight(state: string): boolean {
  return state === 'queued' || state === 'running';
}

/** How often an in-flight simulation is read again. */
export const EXIT_SIMULATION_POLL_MS = 5000;

export type StatusLevel = 'good' | 'warning' | 'serious' | 'critical';

export function exitSimulationLevel(state: string): StatusLevel {
  if (state === 'succeeded') return 'good';
  if (state === 'failed') return 'critical';
  if (isExitSimulationInFlight(state)) return 'warning';
  return 'serious';
}

// ── refusals ─────────────────────────────────────────────────────────────────

const PREFIX = 'planning.exit.consolidation.simulation.';

/** The simulation routes' own refusals, each a sentence in the catalogue. */
export const EXIT_SIMULATION_REFUSAL_KEY: Readonly<Record<string, string>> = {
  [`${PREFIX}no_live_window`]: 'exit.consolidationSimulate.refusal.noLiveWindow',
  [`${PREFIX}perimeter_unknown`]: 'exit.consolidationSimulate.refusal.perimeterUnknown',
  [`${PREFIX}capacity_exhausted`]: 'exit.consolidationSimulate.refusal.capacity',
  [`${PREFIX}not_found`]: 'exit.consolidationSimulate.refusal.notFound',
  [`${PREFIX}unavailable`]: 'exit.consolidationSimulate.refusal.unavailable',
  [`${PREFIX}not_executable`]: 'exit.consolidationSimulate.refusal.notExecutable',
};

/** The sentence a refusal reads as, or `null` for one this module does not own. */
export function exitSimulationRefusalKey(error: unknown): string | null {
  if (!(error instanceof ApiProblemError)) return null;
  return EXIT_SIMULATION_REFUSAL_KEY[error.code] ?? null;
}

/** The scheduled cycle has priority over a simulation (409, `cycle` and `tryAfter` members). */
export const EXIT_CYCLE_WINDOW_CODE = `${PREFIX}cycle_window`;

export type ExitCycleWindowRefusal =
  | { readonly kind: 'tryAfter'; readonly cycle: string; readonly tryAfter: string }
  | { readonly kind: 'cycleRunning'; readonly cycle: string };

export function exitCycleWindowRefusal(error: unknown): ExitCycleWindowRefusal | null {
  if (!(error instanceof ApiProblemError) || error.problem.code !== EXIT_CYCLE_WINDOW_CODE) return null;
  const cycle = typeof error.problem['cycle'] === 'string' ? error.problem['cycle'] : '';
  const tryAfter = error.problem['tryAfter'];
  return typeof tryAfter === 'string' && !Number.isNaN(Date.parse(tryAfter))
    ? { kind: 'tryAfter', cycle, tryAfter }
    : { kind: 'cycleRunning', cycle };
}

// ── a run's perimeter, in the run picker ─────────────────────────────────────

export type RunPerimeterLabel =
  | { readonly key: string; readonly values: Readonly<Record<string, number>> }
  | null;

/**
 * What a consolidation run says about its perimeter beside its name. A simulated run always says
 * so — «Simulation · pilot doors» or «Simulation · all doors» — because nothing on it can be
 * decided. A live run over the pilot's doors says how many; a live run over all doors says nothing.
 */
export function runPerimeterLabel(
  run: Pick<ConsolidationRunSummary, 'simulation_id' | 'perimeter' | 'perimeter_door_count'>,
): RunPerimeterLabel {
  const pilot = run.perimeter === 'pilot';
  if (isSimulatedRun(run)) {
    return { key: pilot ? 'exit.consolidation.run.simulationPilot' : 'exit.consolidation.run.simulationAll', values: {} };
  }
  if (pilot) {
    const count = run.perimeter_door_count;
    return typeof count === 'number'
      ? { key: 'exit.consolidation.run.pilotDoors', values: { count } }
      : { key: 'exit.consolidation.run.pilotDoorsUncounted', values: {} };
  }
  return null;
}

/** A run a simulation wrote. Nothing on it is executable. */
export function isSimulatedRun(run: Pick<ConsolidationRunSummary, 'simulation_id'> | undefined | null): boolean {
  const id = run?.simulation_id;
  return typeof id === 'string' && id !== '';
}

// ── the launched simulation, on the compose page (owner ruling 2026-10-20) ────

/**
 * The simulation launched from the compose page: launching keeps the form there, and the URL names
 * what was launched so the page survives a reload. Its own key — not Simulate's `?simulation=` —
 * because the rail carries every key from page to page.
 */
export const EXIT_SIMULATION_PARAM = 'exitSimulation';

/** The simulation the compose page names, or `null`. Pure. */
export function readLaunchedExitSimulation(search: string): string | null {
  const value = new URLSearchParams(search).get(EXIT_SIMULATION_PARAM)?.trim() ?? '';
  return value === '' ? null : value;
}

/** The search string with the launched simulation set, or removed on `null`. Pure. */
export function withLaunchedExitSimulation(search: string, simulationId: string | null): string {
  const next = new URLSearchParams(search);
  if (simulationId === null) next.delete(EXIT_SIMULATION_PARAM);
  else next.set(EXIT_SIMULATION_PARAM, simulationId);
  const query = next.toString();
  return query === '' ? CONSOLIDATION_SIMULATE_PATH : `${CONSOLIDATION_SIMULATE_PATH}?${query}`;
}

/** The compose page on a simulation's question — its perimeter — for «Modifica e rilancia». Pure. */
export function exitComposeAgainHref(simulationId: string): string {
  return withLaunchedExitSimulation('', simulationId);
}

/** Failed or expired: the question may be asked again. */
export function mayAskExitAgain(state: string): boolean {
  return state === 'failed' || state === 'expired';
}

/**
 * Why a simulation gave no answer, as a sentence — never the platform's code (owner ruling
 * 2026-10-20). The code is the run's own failure (the runner's `<code>:` or the exception's type),
 * its declared refusal or skip, or the simulation's own `exit_simulation_decision_run_not_queued`.
 * A code this build does not know reads as "stopped for a reason it did not describe".
 */
export const EXIT_SIMULATION_FAILURE_KEY: Readonly<Record<string, string>> = {
  exit_simulation_decision_run_not_queued: 'exit.consolidationSimulate.failure.decisionNotQueued',
  runner_lost: 'exit.consolidationSimulate.failure.runnerLost',
  job_sources_unavailable: 'exit.consolidationSimulate.failure.sourcesUnavailable',
  job_sources_unwired: 'exit.consolidationSimulate.failure.sourcesUnwired',
  job_unregistered: 'exit.consolidationSimulate.failure.unregistered',
  module_gate_unreadable: 'exit.consolidationSimulate.failure.moduleGate',
};

/** The sentence a terminal simulation without an answer reads as; `null` while it can still answer. */
export function exitSimulationFailureKey(state: string, failureCode: string | null): string | null {
  if (state === 'expired') return 'exit.consolidationSimulate.failure.expired';
  if (state !== 'failed') return null;
  if (failureCode === null || failureCode === '') return 'exit.consolidationSimulate.failure.unstated';
  return EXIT_SIMULATION_FAILURE_KEY[failureCode] ?? 'exit.consolidationSimulate.failure.unexplained';
}
